import React from "react";
import { useNavigate } from "react-router-dom";
import { Vg2Shell } from "./Vg2Shell";
import { getPaCases } from "@/lib/vg2PaWorkspace";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * PA Workspace home.
 *
 * LAUNCH-C2 — authentication is the real PA login (JWT). The server
 * resolves the PA identity from the Bearer token; only cases granted to
 * that PA are returned. No preview identity, no X-PA-Id.
 */
const PA_ROLES = ["admin", "fpa", "pa", "standard"];

export default function PaWorkspace() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const isPa = !!user && PA_ROLES.includes(user.ruolo);

  const [view, setView] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState("ALL");

  const load = React.useCallback(async () => {
    if (!isPa) { setView(null); return; }
    setError(null);
    try {
      const v = await getPaCases();
      setView(v);
    } catch (e) {
      setError(e);
    }
  }, [isPa]);
  React.useEffect(() => { load(); }, [load]);

  const filteredItems = React.useMemo(() => {
    if (!view?.items) return [];
    if (statusFilter === "ALL") return view.items;
    return view.items.filter((i) => i.pa_status === statusFilter);
  }, [view, statusFilter]);

  if (authLoading) {
    return (
      <Vg2Shell>
        <main className="max-w-[900px] mx-auto px-4 py-8">
          <p className="text-sm text-vg2-muted" data-testid="pa-workspace-auth-loading">
            Caricamento…
          </p>
        </main>
      </Vg2Shell>
    );
  }

  if (!isPa) {
    return (
      <Vg2Shell>
        <main className="max-w-[640px] mx-auto px-4 py-10" data-testid="pa-workspace-not-authorized">
          <p className="text-xs uppercase tracking-[0.14em] text-vg2-red mb-1">
            Property Advisor
          </p>
          <h1 className="font-display text-2xl md:text-3xl font-medium mb-3">
            Area riservata ai Property Advisor
          </h1>
          <p className="text-sm text-vg2-ink mb-5">
            Accedi con il tuo account Property Advisor per consultare i casi a
            te assegnati.
          </p>
          <button onClick={() => navigate("/property-advisor/login")}
                  className="px-4 py-2 bg-vg2-red text-white rounded hover:opacity-90"
                  data-testid="pa-workspace-login-btn">
            Accedi come Property Advisor
          </button>
        </main>
      </Vg2Shell>
    );
  }

  return (
    <Vg2Shell>
      <main className="max-w-[900px] mx-auto px-4 py-8" data-testid="pa-workspace-root">
        <header className="mb-6">
          <p className="text-xs uppercase tracking-[0.14em] text-vg2-red mb-1">
            Property Advisor
          </p>
          <h1 className="font-display text-2xl md:text-3xl font-medium">
            I tuoi casi
          </h1>
          <p className="text-sm text-vg2-muted mt-1">
            Vista operativa sui casi ValutaGenio a te assegnati.
          </p>
        </header>

        {error && error.code === "PA_SUBSCRIPTION_REQUIRED" && (
          <SubscriptionRequiredPanel />
        )}
        {error && error.code !== "PA_SUBSCRIPTION_REQUIRED" && (
          <ErrorPanel error={error} onRetry={load} />
        )}
        {!view && !error && (
          <p className="text-sm text-vg2-muted mt-4" data-testid="pa-workspace-loading">
            Caricamento…
          </p>
        )}

        {view && (
          <>
            <FilterRow value={statusFilter} onChange={setStatusFilter} />
            {filteredItems.length === 0 && (
              <p className="text-sm text-vg2-muted mt-4" data-testid="pa-workspace-empty">
                {view.items?.length
                  ? "Nessun caso corrisponde al filtro corrente."
                  : "Non hai ancora casi assegnati."}
              </p>
            )}
            <ul className="mt-4 space-y-4" data-testid="pa-workspace-case-list">
              {filteredItems.map((i) => (
                <CaseCard key={i.case_id} item={i}
                          onOpen={() => navigate(i.detail_route)} />
              ))}
            </ul>
          </>
        )}
      </main>
    </Vg2Shell>
  );
}


function FilterRow({ value, onChange }) {
  const opts = [
    { v: "ALL", l: "Tutti" },
    { v: "NEW", l: "Nuovi" },
    { v: "READY_FOR_CALL", l: "Pronti per la call" },
    { v: "REASSESSMENT_REQUIRED", l: "Da riesaminare" },
    { v: "READY_FOR_EXECUTION", l: "Pronti per esecuzione" },
    { v: "CLOSED", l: "Chiusi" },
  ];
  return (
    <div className="flex flex-wrap gap-2" data-testid="pa-workspace-filters">
      {opts.map((o) => (
        <button
          key={o.v} onClick={() => onChange(o.v)}
          data-testid={`pa-workspace-filter-${o.v}`}
          className={`px-3 py-1 rounded text-sm border ${
            value === o.v
              ? "border-vg2-red text-vg2-red"
              : "border-vg2-line text-vg2-muted hover:text-vg2-ink"
          }`}>
          {o.l}
        </button>
      ))}
    </div>
  );
}


const STATUS_LABELS_IT = {
  NEW: "Nuovo",
  TO_REVIEW: "Da rivedere",
  READY_FOR_CALL: "Pronto per la call",
  CALL_COMPLETED: "Call svolta",
  REASSESSMENT_REQUIRED: "Da riesaminare",
  READY_FOR_EXECUTION: "Pronto per esecuzione",
  CLOSED: "Chiuso",
};


function CaseCard({ item, onOpen }) {
  return (
    <li className="border border-vg2-line rounded p-4"
        data-testid={`pa-case-${item.case_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="pr-3">
          <p className="text-xs text-vg2-muted mb-1"
             data-testid={`pa-case-owner-${item.case_id}`}>
            {item.owner_display_label}
          </p>
          <p className="font-display font-medium mb-1"
             data-testid={`pa-case-property-${item.case_id}`}>
            {item.property_display_label}
          </p>
          <p className="text-sm text-vg2-ink mb-2">
            Obiettivo: {item.objective_summary}
            {item.selected_direction_label && (
              <> · Direzione: {item.selected_direction_label}</>
            )}
          </p>
          <p className="text-sm text-vg2-red font-medium"
             data-testid={`pa-case-next-${item.case_id}`}>
            {item.next_professional_action_label}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`text-xs px-2 py-1 rounded ${
            item.reassessment_required
              ? "bg-vg2-red text-white"
              : "bg-vg2-subtle text-vg2-ink"
          }`}
                data-testid={`pa-case-status-${item.case_id}`}>
            {STATUS_LABELS_IT[item.pa_status] || item.pa_status}
          </span>
          <button onClick={onOpen}
                  className="px-3 py-1 border border-vg2-red text-vg2-red rounded hover:bg-vg2-red hover:text-white text-sm"
                  data-testid={`pa-case-open-${item.case_id}`}>
            Apri caso
          </button>
        </div>
      </div>
    </li>
  );
}


const PA_SUB_PLANS = [
  { id: "mensile", label: "Mensile", price: "79€", period: "/mese" },
  { id: "semestrale", label: "Semestrale", price: "395€", period: " ogni 6 mesi" },
  { id: "annuale", label: "Annuale", price: "711€", period: "/anno" },
];

function SubscriptionRequiredPanel() {
  const [plan, setPlan] = React.useState("annuale");
  const [loading, setLoading] = React.useState(false);

  const subscribe = async () => {
    setLoading(true);
    try {
      const r = await api.post("/billing/checkout", {
        origin_url: window.location.origin,
        plan,
        product: "pa_subscription",
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
        return;
      }
      if (r.data?.already_active) {
        toast.success("Abbonamento già attivo. Ricarico i tuoi casi…");
        window.location.reload();
        return;
      }
      toast.error("Impossibile avviare il pagamento. Riprova.");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Errore avvio pagamento.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-vg2-line rounded-lg p-5 mt-4"
         data-testid="pa-subscription-required">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-red mb-1">
        Abbonamento richiesto
      </p>
      <h2 className="font-display text-xl font-medium mb-2">
        Attiva l'abbonamento Property Advisor
      </h2>
      <p className="text-sm text-vg2-ink mb-4">
        Per accedere ai casi assegnati e alle funzioni riservate ai Property
        Advisor è necessario un abbonamento attivo. Scegli un piano e completa
        il pagamento in modo sicuro tramite Stripe.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4"
           data-testid="pa-subscription-plans">
        {PA_SUB_PLANS.map((p) => {
          const active = plan === p.id;
          return (
            <button key={p.id} type="button" onClick={() => setPlan(p.id)}
                    data-testid={`pa-sub-plan-${p.id}`}
                    className={`text-left rounded-lg p-3 border-2 transition-colors ${
                      active ? "border-vg2-red" : "border-vg2-line hover:border-vg2-muted"
                    }`}>
              <p className="font-medium text-vg2-ink">{p.label}</p>
              <p className="text-lg font-bold text-vg2-ink">
                {p.price}<span className="text-xs font-normal text-vg2-muted">{p.period}</span>
              </p>
            </button>
          );
        })}
      </div>
      <button onClick={subscribe} disabled={loading}
              className="px-4 py-2 bg-vg2-red text-white rounded hover:opacity-90 disabled:opacity-60"
              data-testid="pa-subscription-pay-btn">
        {loading ? "Reindirizzamento…" : "Attiva abbonamento"}
      </button>
    </div>
  );
}


function ErrorPanel({ error, onRetry }) {  return (
    <div className="border border-vg2-line rounded p-4 mt-4"
         data-testid="pa-workspace-error">
      <p className="text-sm text-vg2-ink mb-3">
        {error?.code === "PA_ACCESS_INVALID"
          ? "Non hai accesso a questo caso."
          : (error?.status === 401 || error?.code === "PA_TOKEN_EXPIRED")
            ? "Sessione scaduta. Accedi di nuovo come Property Advisor."
            : (error?.message || "Errore imprevisto.")}
      </p>
      <button onClick={onRetry}
              className="px-4 py-2 border border-vg2-red text-vg2-red rounded hover:bg-vg2-red hover:text-white"
              data-testid="pa-workspace-retry-btn">
        Riprova
      </button>
    </div>
  );
}
